import React, { useState, useEffect } from 'react';
import { Row, Col, FormGroup, Label, Button, ButtonGroup, UncontrolledTooltip, Input } from 'reactstrap';
import { SimpleForm, SelectField, ProductImageField, AmountField } from 'components/forms';
import { validateFields, validUrl } from 'components/forms/validators';
import { Field as FormField } from 'react-form';
import Loader from 'components/Loader';
import __ from 'utils/i18n';
import { useSelector } from 'react-redux';
import { fetchResourceRP } from '../../../_shared/fetchResourceRP';
import { createPlan, updatePlan } from '../../_shared/resources';
import { notification } from 'utils/notifications';
import gtmEvents from 'utils/gtmEvents';

const MAX_PLAN_AMOUNT = 9999999.99; // Máximo permitido: 9.999.999,99
const MAX_PLAN_INTEGER_DIGITS = 7; // Cantidad máxima de dígitos enteros para 9.999.999
const MAX_PLAN_AMOUNT_DIGITS = 9; // PR-135: Max 9 dígitos totales - evita montos como 0,000000000000000001234567
const MAX_PLAN_DESCRIPTION_LENGTH = 500; 

const validations = [
  { field: 'name', required: 1 },
  { field: 'pricingType', required: 1 },
  { field: 'frequency', required: 1 },
  { 
    field: 'planPrice', 
    custom: (value, values) => {
      // Validar que el monto sea requerido y mayor a 0 si es Fixed
      if (values.pricingType === 'Fixed') {
        if (!value?.amount) {
          return 'El monto es requerido para planes de precio fijo';
        }

        // Convertir el monto formateado a número para validar
        const numericAmount = __.revertNumberFormat(value.amount);

        // Verificar que sea un número válido
        if (numericAmount === null || numericAmount === undefined || isNaN(numericAmount)) {
          return 'El monto debe ser un número válido';
        }

        // Validar que sea mayor a 0
        if (numericAmount <= 0) {
          return 'El monto debe ser mayor a 0';
        }

        // Validar que el monto no exceda el máximo permitido (9.999.999,99)
        if (numericAmount > MAX_PLAN_AMOUNT) {
          const formattedMaxAmount = __.number(MAX_PLAN_AMOUNT, 'decimal');
          return __('validators.maxPlanAmount', { maxAmount: formattedMaxAmount });
        }

        // PR-135: Validar máx 9 dígitos totales - evita montos como 0,000000000000000001234567
        const amountStr = String(value?.amount || '').replace(/\s/g, '');
        const digitCount = (amountStr.replace(/\D/g, '') || '').length;
        if (digitCount > MAX_PLAN_AMOUNT_DIGITS) {
          return __('validators.maxAmountDigits', { max: MAX_PLAN_AMOUNT_DIGITS });
        }
      }
      return null;
    }
  },
  { field: 'redirectUrl', isUrl: 1 },
];

export const PlanPanel = ({ data, onSave, hidePanel, showDetails }) => {
  const { token } = useSelector((state) => state.api);
  const user = useSelector((state) => state.login.user);
  
  const [planData, setPlanData] = useState({
    planId: '',
    name: '',
    description: '',
    pricingType: 'Fixed',
    planPrice: {
      amount: 0,
      currency: 'ARS',
    },
    frequency: 'Monthly',
    billingDay: 'subscription', // Por defecto "subscription" para Fixed
    billingMonth: null,
    duration: null,
    unlimitedDuration: true,
    trialPeriodDays: 0,
    hasTrial: false,
    redirectUrl: '',
    status: 'Active',
    planImage: {},
  });

  const [error, setError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [durationError, setDurationError] = useState(false);
  const [trialError, setTrialError] = useState(false);
  const [planImage, setPlanImage] = useState({});
  const [urlError, setUrlError] = useState(null);
  const MAX_DURATION = 50;
  const MAX_TRIAL_DAYS = 365;

  useEffect(() => {
    if (data && data.planId) {
      const imageData = data.planImage || {};
      setPlanData({
        planId: data.planId,
        name: data.name || '',
        description: data.description || '',
        pricingType: data.pricingType || 'Fixed',
        planPrice: {
          amount: data.amount || 0,
          currency: data.currency || 'ARS',
        },
        frequency: data.frequency || '',
        billingDay: data.billingDay || 'subscription',
        billingMonth: data.billingMonth || null,
        duration: data.duration || null,
        unlimitedDuration: !data.duration,
        trialPeriodDays: data.trialPeriodDays || 0,
        hasTrial: (data.trialPeriodDays || 0) > 0,
        redirectUrl: data.redirectUrl || '',
        status: data.status || 'Active',
        planImage: imageData,
      });
      setPlanImage(imageData);
    } else {
      setPlanData({
        planId: '',
        name: '',
        description: '',
        pricingType: 'Fixed',
        planPrice: {
          amount: 0,
          currency: 'ARS',
        },
        frequency: 'Monthly',
        billingDay: 'subscription',
        billingMonth: null,
        duration: null,
        unlimitedDuration: true,
        trialPeriodDays: 0,
        hasTrial: false,
        redirectUrl: '',
        status: 'Active',
        planImage: {},
      });
      setPlanImage({});
    }
  }, [data]);

  const handleSubmit = async (values) => {
    if (values.hasTrial) {
      const days = parseInt(values.trialPeriodDays, 10);
      if (!days || days < 1 || days > MAX_TRIAL_DAYS) {
        setTrialError(true);
        return;
      }
    }

    setPlanData(values);
    setIsFetching(true);
    setError(false);

    const isEditMode = !!(data && data.planId);

    try {
      const frequencyMap = {
        'Daily': 'daily',
        'Weekly': 'weekly',
        'Monthly': 'monthly',
        'Quarterly': 'quarterly',
        'Yearly': 'yearly'
      };
      
      const pricingTypeMap = {
        'Fixed': 'fixed',
        'Variable': 'dynamic'
      };
      
      const pricingType = pricingTypeMap[values.pricingType] || 'fixed';
      const billingFrequency = frequencyMap[values.frequency] || 'monthly';
      
      const cleanAmount = pricingType === 'fixed' && values.planPrice?.amount
        ? __.revertNumberFormat(values.planPrice.amount)
        : null;
      
      // Calcular billing_day según la frecuencia
      let billingDayValue = null;
      if (billingFrequency === 'weekly' && values.billingDay && values.billingDay !== 'subscription') {
        billingDayValue = parseInt(values.billingDay);
      } else if (billingFrequency === 'monthly' && values.billingDay && values.billingDay !== 'subscription') {
        billingDayValue = parseInt(values.billingDay);
      } else if ((billingFrequency === 'quarterly' || billingFrequency === 'yearly') && values.billingDay) {
        billingDayValue = parseInt(values.billingDay);
      }

      // Calcular billing_month solo para trimestral y anual
      let billingMonthValue = null;
      if ((billingFrequency === 'quarterly' || billingFrequency === 'yearly') && values.billingMonth) {
        billingMonthValue = parseInt(values.billingMonth);
      }

      const payload = {
        name: values.name,
        description: values.description || null,
        pricing_type: pricingType,
        amount: cleanAmount,
        currency: (values.planPrice?.currency || 'ARS').toUpperCase(),
        billing_frequency: billingFrequency,
        billing_cycles: values.unlimitedDuration ? null : (values.duration || null),
        trial_period_days: values.hasTrial ? (parseInt(values.trialPeriodDays, 10) || 0) : 0,
        status: 'active',
        redirect_url: values.redirectUrl || null,
        external_reference: null,
        metadata: null,
        merchant_name: user?.accountName || '',
        merchant_fiscal_id: user?.fiscalId || ''
      };

      // Solo agregar billing_day si tiene valor
      if (billingDayValue !== null) {
        payload.billing_day = billingDayValue;
      }

      // Solo agregar billing_month si tiene valor (para trimestral y anual)
      if (billingMonthValue !== null) {
        payload.billing_month = billingMonthValue;
      }
      
      if (!isEditMode && user?.merchantId) {
        payload.merchant_id = String(user.merchantId);
      }

      // Convertir archivo a base64 si existe
      let imageContent = null;
      let imageFileName = null;
      if (planImage.file) {
        const file = planImage.file;
        const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB en bytes (5,242,880 bytes)

        // Validar formato del archivo por extensión
        const fileName = file.name || '';
        const fileExtension = fileName.toLowerCase().split('.').pop();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp'];
        const allowedMimeTypes = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'webp': 'image/webp'
        };

        // Validar extensión
        if (!allowedExtensions.includes(fileExtension)) {
          throw new Error(__('inputs.image.invalidFormat'));
        }

        // Validar tipo MIME
        const expectedMimeType = allowedMimeTypes[fileExtension];
        if (file.type && expectedMimeType && file.type !== expectedMimeType) {
          // Construir mensaje de error con interpolación manual para evitar problemas con i18next
          const errorMessage = __('inputs.image.invalidMimeType', {
            expectedType: expectedMimeType,
            actualType: file.type
          }).replace('{{expectedType}}', expectedMimeType).replace('{{actualType}}', file.type);
          throw new Error(errorMessage);
        }

        // Validar tamaño del archivo
        if (file.size > MAX_FILE_SIZE) {
          const maxSizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(2);
          throw new Error(__('inputs.image.fileSizeExceeded', { maxSize: maxSizeMB }));
        }

        // Validar nombre del archivo
        // Solo el nombre del archivo (sin rutas)
        // No puede contener .., / o \
        const invalidPathChars = ['..', '/', '\\'];
        const hasInvalidChars = invalidPathChars.some(char => fileName.includes(char));
        if (hasInvalidChars) {
          throw new Error(__('inputs.image.invalidFileName'));
        }

        // Debe tener una extensión válida (ya validado arriba, pero verificamos que exista)
        if (!fileExtension || fileName.lastIndexOf('.') === -1) {
          throw new Error(__('inputs.image.missingExtension'));
        }

        // Extraer solo el nombre del archivo (sin rutas por si acaso)
        const cleanFileName = fileName.split(/[/\\]/).pop();

        const reader = new FileReader();
        imageContent = await new Promise((resolve, reject) => {
          reader.onload = () => {
            // Extraer solo el base64 sin el prefijo data:image/...;base64,
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        // Usar el nombre limpio del archivo
        imageFileName = cleanFileName;
      }
      
      const backgroundColor = values.planImage?.color || null;
      
      let deleteImage = false;
      if (isEditMode && planImage.deleteProductImage === true) {
        deleteImage = true;
      }

      let response;
      if (isEditMode) {
        response = await fetchResourceRP(updatePlan(
          data.planId,
          payload,
          imageContent,
          imageFileName,
          backgroundColor,
          deleteImage,
          token
        ));
      } else {
        response = await fetchResourceRP(createPlan(
          payload,
          imageContent,
          imageFileName,
          backgroundColor,
          token
        ));
      }
      setIsFetching(false);

      if (!isEditMode) {
        gtmEvents.recurringPlanCreateSuccess({
          plan_id: response?.planId,
          merchant_id: user?.merchantId,
        });
      }

      if (typeof onSave === 'function') {
        onSave(response, isEditMode);
      }
    } catch (err) {
      console.error('[PlanPanel] Error caught:', err);
      setIsFetching(false);
      if (!isEditMode) {
        gtmEvents.recurringPlanCreateError({
          merchant_id: user?.merchantId,
          error_code: err?.message_code,
        });
      }
      notification.error(`Imagen error: ${err.message}`);
    }
  };

  const frequencyOptions = [
    {
      value: 'Weekly',
      label: __('dashboard.sections.recurringPayments.plans.table.frequencies.weekly'),
      days: '7 días',
    },
    {
      value: 'Monthly',
      label: __('dashboard.sections.recurringPayments.plans.table.frequencies.monthly'),
      days: '30 días',
    },
    {
      value: 'Quarterly',
      label: __('dashboard.sections.recurringPayments.plans.table.frequencies.quarterly'),
      days: '90 días',
    },
    {
      value: 'Yearly',
      label: __('dashboard.sections.recurringPayments.plans.table.frequencies.yearly'),
      days: '365 días',
    },
  ];

  const billingDayOptionsBase = [];
  for (let i = 1; i <= 28; i++) {
    billingDayOptionsBase.push({ value: i.toString(), label: i.toString() });
  }

  const weekDayOptionsBase = [
    { value: '1', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.monday') },
    { value: '2', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.tuesday') },
    { value: '3', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.wednesday') },
    { value: '4', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.thursday') },
    { value: '5', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.friday') },
    { value: '6', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.saturday') },
    { value: '7', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.sunday') }
  ];

  // Función para obtener opciones de billing day según el pricing type
  const getBillingDayOptions = (pricingType, isWeekly = false) => {
    const baseOptions = isWeekly ? weekDayOptionsBase : billingDayOptionsBase;
    
    // Solo agregar "Día de la suscripción" si es Fixed
    if (pricingType === 'Fixed') {
      return [
        { value: 'subscription', label: __('dashboard.sections.recurringPayments.plans.form.billingDay.subscription') },
        ...baseOptions
      ];
    }
    
    return baseOptions;
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString(),
    label: __(`dashboard.sections.recurringPayments.plans.form.months.${
      ['january', 'february', 'march', 'april', 'may', 'june',
       'july', 'august', 'september', 'october', 'november', 'december'][i]
    }`)
  }));

  const calculateFirstPaymentDate = (frequency, billingDay, billingMonth) => {
    const today = new Date();
    let firstPaymentDate = new Date(today);
    
    switch (frequency) {
      case 'Weekly':
        // Si es 'subscription', el cobro será hoy
        if (!billingDay || billingDay === 'subscription') {
          firstPaymentDate = today;
          break;
        }
        // billingDay: 1=Lunes, 7=Domingo
        // Calcular el próximo día de la semana elegido
        const currentDayOfWeek = today.getDay() || 7; // Convertir 0 (domingo) a 7
        const targetDayOfWeek = parseInt(billingDay);
        // Calcular días hasta el próximo día de la semana elegido
        let daysUntilTarget = targetDayOfWeek - currentDayOfWeek;
        if (daysUntilTarget <= 0) {
          daysUntilTarget += 7; // Si ya pasó esta semana, ir a la próxima semana
        }
        firstPaymentDate.setDate(today.getDate() + daysUntilTarget);
        break;
        
      case 'Quarterly':
      case 'Yearly':
        const targetMonth = parseInt(billingMonth) - 1; // 0-indexed
        const targetDay = Math.min(parseInt(billingDay), 28);
        
        firstPaymentDate = new Date(today.getFullYear(), targetMonth, targetDay);
        
        // Si la fecha ya pasó, mover al próximo ciclo
        if (firstPaymentDate <= today) {
          if (frequency === 'Quarterly') {
            firstPaymentDate.setMonth(firstPaymentDate.getMonth() + 3);
          } else {
            firstPaymentDate.setFullYear(firstPaymentDate.getFullYear() + 1);
          }
        }
        break;
        
      case 'Monthly':
        // Si es 'subscription', el cobro será hoy
        if (!billingDay || billingDay === 'subscription') {
          firstPaymentDate = today;
          break;
        }
        const day = Math.min(parseInt(billingDay), 28);
        firstPaymentDate = new Date(today.getFullYear(), today.getMonth(), day);
        if (firstPaymentDate <= today) {
          firstPaymentDate.setMonth(firstPaymentDate.getMonth() + 1);
        }
        break;

      default:
        break;
    }
    
    // Formatear fecha manualmente para evitar escape de caracteres
    const day = String(firstPaymentDate.getDate()).padStart(2, '0');
    const month = String(firstPaymentDate.getMonth() + 1).padStart(2, '0');
    const year = firstPaymentDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getQuarterlyMonths = (startMonth) => {
    const monthNames = [
      __('dashboard.sections.recurringPayments.plans.form.months.january'),
      __('dashboard.sections.recurringPayments.plans.form.months.february'),
      __('dashboard.sections.recurringPayments.plans.form.months.march'),
      __('dashboard.sections.recurringPayments.plans.form.months.april'),
      __('dashboard.sections.recurringPayments.plans.form.months.may'),
      __('dashboard.sections.recurringPayments.plans.form.months.june'),
      __('dashboard.sections.recurringPayments.plans.form.months.july'),
      __('dashboard.sections.recurringPayments.plans.form.months.august'),
      __('dashboard.sections.recurringPayments.plans.form.months.september'),
      __('dashboard.sections.recurringPayments.plans.form.months.october'),
      __('dashboard.sections.recurringPayments.plans.form.months.november'),
      __('dashboard.sections.recurringPayments.plans.form.months.december')
    ];
    
    const months = [];
    let month = parseInt(startMonth) - 1; // 0-indexed
    
    for (let i = 0; i < 4; i++) {
      months.push(monthNames[month % 12]);
      month += 3;
    }
    
    return months.join(', ');
  };

  const isReady = data?.planId ? planData.planId === data.planId : planData.planId === '';

  if (!isReady) {
    return null;
  }

  return (
    <SimpleForm
      key={data?.planId || 'new'}
      defaultValues={planData}
      validateOnSubmit={true}
      validate={validateFields(validations)}
      reset={!showDetails}
      onChange={() => setError(false)}
      onSubmit={handleSubmit}
    >
      {({ values, touched, setValue, formApi }) => {
        const isFixedPrice = values.pricingType === 'Fixed';
        const isEditMode = !!(data && data.planId);
        
        return (
          <div className="panel-flex" style={{ position: 'relative' }}>
            {isFetching && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'white',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Loader absolute={false} />
                <h4 className="mt-3 mb-2 font-weight-bold">
                  {isEditMode 
                    ? __('dashboard.sections.recurringPayments.plans.savingPlan')
                    : __('dashboard.sections.recurringPayments.plans.creatingPlan')}
                </h4>
                <p className="text-muted">
                  {__('dashboard.sections.recurringPayments.plans.pleaseWait')}
                </p>
              </div>
            )}
            <div>
              <ProductImageField
                name="planImage"
                data={values}
                setFile={(imageValues) => setPlanImage(imageValues)}
                label={__('dashboard.sections.recurringPayments.plans.form.image')}
                modalTitle={__('inputs.image.editImagePlan')}
                descriptionKey="inputs.image.selectImagePlan"
                deleteTitle={__('inputs.image.deleteImagePlan')}
                confirmDeleteKey="inputs.image.confirmDeleteImagePlan"
              />

              <div className="fields-panel">
                <FormGroup>
                  <Label>{__('dashboard.sections.recurringPayments.plans.form.name')} *</Label>
                  <Input
                    type="text"
                    name="name"
                    placeholder={__('dashboard.sections.recurringPayments.plans.form.namePlaceholder')}
                    value={values.name || ''}
                    onChange={(e) => setValue('name', e.target.value)}
                  />
                </FormGroup>

                {!isEditMode && (
                  <FormGroup>
                    <Label>{__('dashboard.sections.recurringPayments.plans.form.pricingType')}</Label>
                    <ButtonGroup className="w-100">
                      <Button
                        color={values.pricingType === 'Fixed' ? 'primary' : 'secondary'}
                        outline={values.pricingType !== 'Fixed'}
                        onClick={() => {
                          setValue('pricingType', 'Fixed');
                          // Cambiar billingDay a 'subscription' para Fixed
                          if (values.frequency === 'Weekly' || values.frequency === 'Monthly') {
                            setValue('billingDay', 'subscription');
                          }
                        }}
                        style={{ width: '50%' }}
                      >
                        {__('dashboard.sections.recurringPayments.plans.filters.pricingType.fixed')}
                      </Button>
                      <Button
                        color={values.pricingType === 'Variable' ? 'primary' : 'secondary'}
                        outline={values.pricingType !== 'Variable'}
                        onClick={() => {
                          setValue('pricingType', 'Variable');
                          // Cambiar billingDay a '1' para Variable (siempre)
                          if (values.frequency === 'Weekly' || values.frequency === 'Monthly') {
                            setValue('billingDay', '1');
                          }
                        }}
                        style={{ width: '50%' }}
                      >
                        {__('dashboard.sections.recurringPayments.plans.filters.pricingType.variable')}
                      </Button>
                    </ButtonGroup>
                  </FormGroup>
                )}

                {(isFixedPrice || !isEditMode) && (
                  <>
                    {isFixedPrice ? (
                      <AmountField
                        allowEmptyAtStart={true}
                        name="planPrice"
                        type="amount"
                        label={__('dashboard.sections.recurringPayments.plans.form.amount') + ' *'}
                        placeholder="20.000,00"
                        required={isFixedPrice}
                        maxIntegerDigits={MAX_PLAN_INTEGER_DIGITS}
                        maxAmountDigits={MAX_PLAN_AMOUNT_DIGITS}
                      />
                    ) : (
                      !isEditMode && (
                        <div className="alert alert-info d-flex align-items-start" role="alert">
                          <i className="mdi mdi-information mdi-24px mr-3"></i>
                          <span>{__('dashboard.sections.recurringPayments.plans.form.variablePriceInfo')}</span>
                        </div>
                      )
                    )}
                  </>
                )}

                {!isEditMode && (
                  <>
                    <FormGroup>
                      <Label>{__('dashboard.sections.recurringPayments.plans.form.frequency')} *</Label>
                      <SelectField
                        name="frequency"
                        options={frequencyOptions}
                        value={values.frequency}
                        onChange={(value) => {
                          setValue('frequency', value);
                          // Establecer valores por defecto según la frecuencia y pricing type
                          if (value === 'Weekly' || value === 'Monthly') {
                            // Para Weekly y Monthly, depende del pricing type
                            const defaultBillingDay = values.pricingType === 'Fixed' ? 'subscription' : '1';
                            setValue('billingDay', defaultBillingDay);
                          }
                          if (value === 'Quarterly' || value === 'Yearly') {
                            setValue('billingMonth', '1'); // Enero por defecto
                            setValue('billingDay', '1'); // Día 1 por defecto
                          }
                        }}
                        placeholder={__('dashboard.sections.recurringPayments.plans.form.frequencyPlaceholder')}
                        formatOptionLabel={(option) => (
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <span>{option.label}</span>
                            <span className="text-muted small ml-2">{option.days}</span>
                          </div>
                        )}
                      />
                    </FormGroup>

                    {values.frequency === 'Weekly' && (
                      <FormGroup>
                        <Label>
                          {__('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.label')}
                        </Label>
                        <SelectField
                          name="billingDay"
                          options={getBillingDayOptions(values.pricingType, true)}
                          value={values.billingDay || (values.pricingType === 'Fixed' ? 'subscription' : '1')}
                          onChange={(value) => setValue('billingDay', value)}
                        />
                        {values.billingDay && values.billingDay !== 'subscription' && (
                          <div className="alert alert-info d-flex align-items-start mt-2" role="alert">
                            <i className="mdi mdi-information mdi-18px mr-3"></i>
                            <span>El próximo cobro será el {calculateFirstPaymentDate('Weekly', values.billingDay, null)}</span>
                          </div>
                        )}
                      </FormGroup>
                    )}

                    {values.frequency === 'Monthly' && (
                      <FormGroup>
                        <Label>
                          {__('dashboard.sections.recurringPayments.plans.form.billingDay.label')}{' '}
                          <i 
                            id="billingDayTooltip" 
                            className="mdi mdi-help-circle text-muted cursor-pointer" 
                          />
                          <UncontrolledTooltip placement="auto" target="billingDayTooltip">
                            {__('dashboard.sections.recurringPayments.plans.form.billingDay.tooltip')}
                          </UncontrolledTooltip>
                        </Label>
                        <SelectField
                          name="billingDay"
                          options={getBillingDayOptions(values.pricingType, false)}
                          value={values.billingDay || (values.pricingType === 'Fixed' ? 'subscription' : '1')}
                          onChange={(value) => setValue('billingDay', value)}
                        />
                        {values.billingDay && values.billingDay !== 'subscription' && (
                          <div className="alert alert-info d-flex align-items-start mt-2" role="alert">
                            <i className="mdi mdi-information mdi-18px mr-3"></i>
                            <span>El próximo cobro será el {calculateFirstPaymentDate('Monthly', values.billingDay, null)}</span>
                          </div>
                        )}
                      </FormGroup>
                    )}

                    {values.frequency === 'Quarterly' && (
                      <>
                        <FormGroup>
                          <Row>
                            <Col md="6">
                              <Label>
                                {__('dashboard.sections.recurringPayments.plans.form.billingDay.quarterly.month')}
                              </Label>
                              <SelectField
                                name="billingMonth"
                                options={monthOptions}
                                value={values.billingMonth || '1'}
                                onChange={(value) => setValue('billingMonth', value)}
                              />
                            </Col>
                            <Col md="6">
                              <Label>
                                {__('dashboard.sections.recurringPayments.plans.form.billingDay.quarterly.day')}
                              </Label>
                              <SelectField
                                name="billingDay"
                                options={billingDayOptionsBase}
                                value={values.billingDay || '1'}
                                onChange={(value) => setValue('billingDay', value)}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        
                        {values.billingMonth && values.billingDay && (
                          <div className="alert alert-info d-flex align-items-start mt-2" role="alert">
                            <i className="mdi mdi-information mdi-18px mr-3"></i>
                            <div>
                              <div className="mb-1">
                                El próximo cobro será el {calculateFirstPaymentDate('Quarterly', values.billingDay, values.billingMonth)}
                              </div>
                              <div>
                                Se cobrará en: {getQuarterlyMonths(values.billingMonth)}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {values.frequency === 'Yearly' && (
                      <>
                        <FormGroup>
                          <Row>
                            <Col md="6">
                              <Label>
                                {__('dashboard.sections.recurringPayments.plans.form.billingDay.yearly.month')}
                              </Label>
                              <SelectField
                                name="billingMonth"
                                options={monthOptions}
                                value={values.billingMonth || '1'}
                                onChange={(value) => setValue('billingMonth', value)}
                              />
                            </Col>
                            <Col md="6">
                              <Label>
                                {__('dashboard.sections.recurringPayments.plans.form.billingDay.yearly.day')}
                              </Label>
                              <SelectField
                                name="billingDay"
                                options={billingDayOptionsBase}
                                value={values.billingDay || '1'}
                                onChange={(value) => setValue('billingDay', value)}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                        
                        {values.billingMonth && values.billingDay && (
                          <div className="alert alert-info d-flex align-items-start mt-2" role="alert">
                            <i className="mdi mdi-information mdi-18px mr-3"></i>
                            <span>El próximo cobro será el {calculateFirstPaymentDate('Yearly', values.billingDay, values.billingMonth)}</span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                <FormGroup>
                  <Label>{__('dashboard.sections.recurringPayments.plans.form.description')}</Label>
                  <Input
                    type="textarea"
                    name="description"
                    rows="3"
                    maxLength={MAX_PLAN_DESCRIPTION_LENGTH}
                    placeholder={__('dashboard.sections.recurringPayments.plans.form.descriptionPlaceholder')}
                    value={values.description || ''}
                    onChange={(e) => setValue('description', e.target.value.slice(0, MAX_PLAN_DESCRIPTION_LENGTH))}
                  />
                  <small className="text-muted">
                    {(values.description || '').length}/{MAX_PLAN_DESCRIPTION_LENGTH}
                  </small>
                </FormGroup>

                {!isEditMode && (
                  <FormGroup>
                    <Label>
                      {__('dashboard.sections.recurringPayments.plans.form.duration')}{' '}
                      <i 
                        id="durationTooltip" 
                        className="mdi mdi-help-circle text-muted cursor-pointer" 
                      />
                      <UncontrolledTooltip placement="auto" target="durationTooltip">
                        {__('dashboard.sections.recurringPayments.plans.form.durationTooltip')}
                      </UncontrolledTooltip>
                    </Label>
                    <ButtonGroup className="w-100 mb-3">
                      <Button
                        color={values.unlimitedDuration ? 'primary' : 'secondary'}
                        outline={!values.unlimitedDuration}
                        onClick={() => {
                          setValue('unlimitedDuration', true);
                          setValue('duration', null);
                        }}
                        style={{ width: '50%' }}
                      >
                        {__('dashboard.sections.recurringPayments.plans.form.unlimitedDuration')}
                      </Button>
                      <Button
                        color={!values.unlimitedDuration ? 'primary' : 'secondary'}
                        outline={values.unlimitedDuration}
                        onClick={() => setValue('unlimitedDuration', false)}
                        style={{ width: '50%' }}
                      >
                        {__('dashboard.sections.recurringPayments.plans.form.limitedDuration')}
                      </Button>
                    </ButtonGroup>
                    
                    {!values.unlimitedDuration && (
                      <>
                        <Label>
                          {__('dashboard.sections.recurringPayments.plans.form.durationPlaceholder')}{' '}
                          <i 
                            id="durationPlaceholderTooltip" 
                            className="mdi mdi-help-circle text-muted cursor-pointer" 
                          />
                          <UncontrolledTooltip placement="auto" target="durationPlaceholderTooltip">
                            {__('dashboard.sections.recurringPayments.plans.form.durationPlaceholderTooltip')}
                          </UncontrolledTooltip>
                        </Label>
                        <Input
                          type="number"
                          name="duration"
                          placeholder={__('dashboard.sections.recurringPayments.plans.form.durationPlaceholder')}
                          value={values.duration || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || null;
                            if (value && value > MAX_DURATION) {
                              setValue('duration', MAX_DURATION);
                              setDurationError(true);
                            } else {
                              setValue('duration', value);
                              setDurationError(false);
                            }
                          }}
                          min="1"
                          max={MAX_DURATION}
                        />
                        {durationError && (
                          <small className="text-danger d-flex align-items-center mt-1">
                            <i className="mdi mdi-alert-circle mr-1"></i>
                            {__('dashboard.sections.recurringPayments.plans.form.durationMaxError').replace('{n}', MAX_DURATION)}
                          </small>
                        )}
                      </>
                    )}
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>
                    {__('dashboard.sections.recurringPayments.plans.form.trialPeriod')}{' '}
                    <i
                      id="trialPeriodTooltip"
                      className="mdi mdi-help-circle text-muted cursor-pointer"
                    />
                    <UncontrolledTooltip placement="auto" target="trialPeriodTooltip">
                      {__('dashboard.sections.recurringPayments.plans.form.trialPeriodTooltip')}
                    </UncontrolledTooltip>
                  </Label>
                  <ButtonGroup className="w-100 mb-3">
                    <Button
                      color={!values.hasTrial ? 'primary' : 'secondary'}
                      outline={values.hasTrial}
                      onClick={() => {
                        setValue('hasTrial', false);
                        setValue('trialPeriodDays', 0);
                        setTrialError(false);
                      }}
                      style={{ width: '50%' }}
                    >
                      {__('dashboard.sections.recurringPayments.plans.form.noTrial')}
                    </Button>
                    <Button
                      color={values.hasTrial ? 'primary' : 'secondary'}
                      outline={!values.hasTrial}
                      onClick={() => setValue('hasTrial', true)}
                      style={{ width: '50%' }}
                    >
                      {__('dashboard.sections.recurringPayments.plans.form.withTrial')}
                    </Button>
                  </ButtonGroup>

                  {values.hasTrial && (
                    <>
                      <Label>
                        {__('dashboard.sections.recurringPayments.plans.form.trialPeriodDays')}{' '}
                        <i
                          id="trialPeriodDaysTooltip"
                          className="mdi mdi-help-circle text-muted cursor-pointer"
                        />
                        <UncontrolledTooltip placement="auto" target="trialPeriodDaysTooltip">
                          {__('dashboard.sections.recurringPayments.plans.form.trialPeriodDaysTooltip')}
                        </UncontrolledTooltip>
                      </Label>
                      <Input
                        type="number"
                        name="trialPeriodDays"
                        placeholder={__('dashboard.sections.recurringPayments.plans.form.trialPeriodDaysPlaceholder')}
                        value={values.trialPeriodDays || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || 0;
                          if (value > MAX_TRIAL_DAYS) {
                            setValue('trialPeriodDays', MAX_TRIAL_DAYS);
                            setTrialError(true);
                          } else {
                            setValue('trialPeriodDays', value);
                            setTrialError(value < 1);
                          }
                        }}
                        min="1"
                        max={MAX_TRIAL_DAYS}
                      />
                      {trialError && (
                        <small className="text-danger d-flex align-items-center mt-1">
                          <i className="mdi mdi-alert-circle mr-1"></i>
                          {__('dashboard.sections.recurringPayments.plans.form.trialPeriodDaysError').replace('{n}', MAX_TRIAL_DAYS)}
                        </small>
                      )}
                    </>
                  )}
                </FormGroup>

                <FormField field="redirectUrl">
                  {({ value, setValue: setFieldValue, setTouched, setError, error, touched }) => {
                    // Validar manualmente en tiempo real usando el validador de validators.js
                    const validateOnChange = (urlValue) => {
                      // Usar el mismo validador que usa el formulario
                      const validationError = validUrl()(urlValue);
                      
                      // Actualizar estado local y react-form
                      if (validationError) {
                        setUrlError(validationError);
                        setError(validationError);
                      } else {
                        setUrlError(null);
                        setError(undefined);
                      }
                    };

                    // Usar error local si existe, sino usar el de react-form
                    const displayError = urlError || error;
                    const isTouched = touched || urlError !== null;

                    return (
                      <FormGroup className={isTouched && displayError ? 'has-error' : ''}>
                        <Label className={isTouched && displayError ? 'text-danger' : ''}>
                          {__('dashboard.sections.recurringPayments.plans.form.redirectUrl')}{' '}
                          <i 
                            id="redirectUrlTooltip" 
                            className="mdi mdi-help-circle text-muted cursor-pointer" 
                          />
                          <UncontrolledTooltip placement="auto" target="redirectUrlTooltip">
                            {__('dashboard.sections.recurringPayments.plans.form.redirectUrlTooltip')}
                          </UncontrolledTooltip>
                        </Label>
                        <Input
                          type="text"
                          name="redirectUrl"
                          placeholder={__('dashboard.sections.recurringPayments.plans.form.redirectUrlPlaceholder')}
                          value={value || ''}
                          onChange={(e) => {
                            const urlValue = e.target.value;
                            setFieldValue(urlValue);
                            // Marcar como touched y validar mientras escribe
                            if (urlValue.trim() !== '') {
                              setTouched(true);
                              validateOnChange(urlValue);
                            } else {
                              // Si está vacío, limpiar el error (campo opcional)
                              setUrlError(null);
                              setError(undefined);
                            }
                          }}
                          invalid={isTouched && displayError}
                        />
                        {isTouched && displayError && (
                          <small className="form-text text-danger d-flex align-items-center mt-1">
                            <i className="mdi mdi-alert-circle mr-1"></i>
                            {displayError}
                          </small>
                        )}
                      </FormGroup>
                    );
                  }}
                </FormField>
              </div>
            </div>

            <div className="panel-footer">
              {error && (
                <div className="alert alert-danger mb-3" role="alert">
                  {__('errors.generic')}
                </div>
              )}
              <Row>
                <Col md='12'>
                  <Button
                    color="success"
                    type="submit"
                    block={true}
                    disabled={
                      isEditMode 
                        ? isFetching 
                        : (
                            isFetching || 
                            !values.name?.trim() || 
                            !values.frequency ||
                            (isFixedPrice && (!values.planPrice?.amount || values.planPrice.amount <= 0))
                          )
                    }
                  >
                    {isEditMode
                      ? __('actions.save')
                      : __('actions.create')}
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </SimpleForm>
  );
};
